import {ApiConfig} from '../config/ApiConfig';
import {getAccessToken} from '../helpers/storage';
import {getDeviceHeaders} from '../helpers/deviceInfo';

/**
 * @typedef {Object} UpdateNotificationTokenPayload
 * @property {string} notificationToken
 * @property {'android'} [platform]
 * @property {string} [deviceId]
 * @property {string} [deviceName]
 * @property {string} [appVersion]
 * @property {string} [osVersion]
 * @property {string} [locale]
 * @property {string} [timezone]
 */

const UPDATE_NOTIFICATION_TOKEN_URL = `${ApiConfig.apiURL}/api/user/me/updateToken`;
const CURRENT_USER_URL = `${ApiConfig.apiURL}/api/user/me`;

const buildErrorFromResponse = async (response) => {
    let error;
    let serverCode = null;
    try {
        const responseBody = await response.json();
        const message = responseBody?.message || responseBody?.error || response.statusText;
        serverCode = responseBody?.code || null;
        error = new Error(message || `Request failed with status ${response.status}`);
    } catch (_error) {
        error = new Error(response.statusText || `Request failed with status ${response.status}`);
    }
    // Expose the HTTP status so callers can tell an auth failure (401/403 — the
    // session is dead, log out) apart from a transient/network error (keep the
    // cached session, retry later).
    error.status = response.status;
    // Preserve the backend's semantic code (e.g. DEVICE_LOGGED_OUT, ACCESS_REVOKED)
    // so the session guard can show the precise logout reason instead of a generic
    // "session expired". `code` stays AUTH_REQUIRED for backward compatibility.
    if (serverCode) {
        error.serverCode = serverCode;
    }
    if (response.status === 401 || response.status === 403) {
        error.code = 'AUTH_REQUIRED';
    }
    return error;
};

/**
 * @param {UpdateNotificationTokenPayload} payload
 */
export async function updateNotificationToken(payload) {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        const authError = new Error('Authentication required');
        authError.code = 'AUTH_REQUIRED';
        throw authError;
    }

    const response = await fetch(UPDATE_NOTIFICATION_TOKEN_URL, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        throw await buildErrorFromResponse(response);
    }

    if (response.status === 204) {
        return true;
    }

    const responseText = await response.text();
    if (!responseText) {
        return true;
    }

    try {
        return JSON.parse(responseText);
    } catch (_error) {
        return responseText;
    }
}

const parseApiResponse = async (response) => {
    if (!response.ok) {
        throw await buildErrorFromResponse(response);
    }

    if (response.status === 204) {
        return true;
    }

    const responseText = await response.text();
    if (!responseText) {
        return true;
    }

    try {
        return JSON.parse(responseText);
    } catch (_error) {
        return responseText;
    }
};

const authHeaders = async () => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        const authError = new Error('Authentication required');
        authError.code = 'AUTH_REQUIRED';
        throw authError;
    }

    // Attach the stable per-install device id (x-device-id / x-device-name) on
    // these authenticated calls — critically the `/me` session heartbeat — so the
    // backend can bind this device and enforce a remote "log out this device":
    // once the device row is disabled, its next /me returns 401 DEVICE_LOGGED_OUT
    // and the session guard signs it out. Best-effort: never block the request if
    // the device id can't be resolved.
    let deviceHeaders = {};
    try {
        deviceHeaders = await getDeviceHeaders();
    } catch (_error) {
        // Non-fatal — proceed without the device headers.
    }

    return {
        'Content-Type': 'application/json',
        ...deviceHeaders,
        Authorization: `Bearer ${accessToken}`,
    };
};

export async function getCurrentUser() {
    const response = await fetch(CURRENT_USER_URL, {
        method: 'GET',
        headers: await authHeaders(),
    });

    return parseApiResponse(response);
}

export async function updateNotificationPreferences(notificationPreferences) {
    const response = await fetch(CURRENT_USER_URL.replace(/\/me$/, ''), {
        method: 'PUT',
        headers: await authHeaders(),
        body: JSON.stringify({notificationPreferences}),
    });

    return parseApiResponse(response);
}

const NOTIFICATIONS_URL = `${ApiConfig.apiURL}/api/notifications`;
const ASSIGNABLE_USERS_URL = `${ApiConfig.apiURL}/api/support/assignable-users`;

const buildQuery = (params = {}) => {
    const entries = Object.entries(params).filter(
        ([, value]) => value !== undefined && value !== null && value !== ''
    );

    if (entries.length === 0) {
        return '';
    }

    return `?${entries.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('&')}`;
};

/**
 * Unread counters for the app icon badge in ONE call.
 *
 * `GET /api/notifications/badge` → `{notifications, mail, total}`; both counters are
 * index-covered counts, so this is cheap enough to run on every foreground/poll.
 * Throws like every other call here (404 on a backend older than the endpoint —
 * callers fall back to the per-source counts).
 *
 * @returns {Promise<{notifications: number, mail: number, total: number}>}
 */
export async function getNotificationBadgeCounts() {
    const response = await fetch(`${NOTIFICATIONS_URL}/badge`, {
        method: 'GET',
        headers: await authHeaders(),
    });

    return parseApiResponse(response);
}

/** List the current user's notifications. */
export async function listNotifications(params = {}) {
    const response = await fetch(`${NOTIFICATIONS_URL}${buildQuery(params)}`, {
        method: 'GET',
        headers: await authHeaders(),
    });

    return parseApiResponse(response);
}

/**
 * Read ONE notification, always including its `metadata` (the list endpoint omits
 * metadata unless asked). Backs the notification detail screen, which re-reads the
 * record it was handed so a stale cached row can never render a wrong page.
 *
 * @param {string} id Notification `_id`.
 * @returns {Promise<Object>} The notification document.
 */
export async function getNotification(id) {
    const response = await fetch(`${NOTIFICATIONS_URL}/${encodeURIComponent(id)}`, {
        method: 'GET',
        headers: await authHeaders(),
    });

    return parseApiResponse(response);
}

/** Mark a single notification as read. */
export async function markNotificationAsRead(id) {
    const response = await fetch(`${NOTIFICATIONS_URL}/${id}/read`, {
        method: 'PATCH',
        headers: await authHeaders(),
    });

    return parseApiResponse(response);
}

/** Mark a single notification as unread. */
export async function markNotificationAsUnread(id) {
    const response = await fetch(`${NOTIFICATIONS_URL}/${id}/unread`, {
        method: 'PATCH',
        headers: await authHeaders(),
    });

    return parseApiResponse(response);
}

/** Mark every notification as read. */
export async function markAllNotificationsAsRead() {
    const response = await fetch(`${NOTIFICATIONS_URL}/read-all`, {
        method: 'PATCH',
        headers: await authHeaders(),
    });

    return parseApiResponse(response);
}

/** Delete a single notification. */
export async function deleteNotification(id) {
    const response = await fetch(`${NOTIFICATIONS_URL}/${id}`, {
        method: 'DELETE',
        headers: await authHeaders(),
    });

    return parseApiResponse(response);
}

/** Delete all notifications ("clear all"), optionally only read/unread ones. */
export async function clearAllNotifications(params = {}) {
    const response = await fetch(`${NOTIFICATIONS_URL}${buildQuery(params)}`, {
        method: 'DELETE',
        headers: await authHeaders(),
    });

    return parseApiResponse(response);
}

/** Notify users mentioned (@name) in a comment / note. */
export async function sendMentionNotifications(payload) {
    const response = await fetch(`${NOTIFICATIONS_URL}/mention`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(payload),
    });

    return parseApiResponse(response);
}

/** Restaurant-scoped user search for @mention autocomplete. */
export async function searchMentionableUsers(q = '', limit = 8) {
    const response = await fetch(`${ASSIGNABLE_USERS_URL}${buildQuery({q, limit})}`, {
        method: 'GET',
        headers: await authHeaders(),
    });

    return parseApiResponse(response);
}



/**
 * Answer a mailbox-share request received as an in-app notification
 * (`metadata.kind === 'mail_share_request'`). Uses a raw authenticated fetch
 * (like the rest of this module) so it keeps working against a spec that does
 * not yet list `respondToMailShareRequest`.
 *
 * @param {string} shareId Share id from `metadata.shareId`.
 * @param {'allow'|'deny'} decision Owner's decision.
 * @returns {Promise<{status: 'allowed'|'denied', share: Object}>}
 * @throws {Error} With `.status` (HTTP) and `.serverMsg` (e.g. `MAIL_SHARE_REQUEST_CLOSED`).
 */
export async function respondToMailShareRequest(shareId, decision) {
    const response = await fetch(
        `${ApiConfig.apiURL}/api/mail/share-requests/${encodeURIComponent(shareId)}/respond`,
        {
            method: 'POST',
            headers: {...(await authHeaders()), 'Content-Type': 'application/json'},
            body: JSON.stringify({decision}),
        },
    );
    if (response.ok) {
        return parseApiResponse(response);
    }
    let payload = null;
    try {
        payload = await response.json();
    } catch (_error) {
        payload = null;
    }
    const error = new Error(payload?.message || payload?.msg || payload?.error || `Request failed with status ${response.status}`);
    error.status = response.status;
    error.serverMsg = String(payload?.msg || payload?.message || '');
    throw error;
}
